import Config from '@config';
import type {
  OrganizationReadModel,
  UpdateOrganizationRequest,
} from '@gasbi/shared/web/RbacOrganization';
import { createApi } from '@reduxjs/toolkit/query/react';

import { organizationsApi } from '@/features/organizations/organizationsApi';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export interface UpdateRbacOrganizationArgs {
  data: UpdateOrganizationRequest;
  logo?: File | null;
}

/**
 * BMS's organization of the shared RBAC (`/bims/api/organizations/{id}`, GSB-8317) for the shared organization page
 * (@gasbi/shared/web/RbacOrganization). The ГРМ card, the list and the organization context stay on
 * `organizationsApi` (billing-organizations).
 */
export const organizationApi = createApi({
  reducerPath: 'rbacOrganizationApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_CLIENT_URL}/organizations`,
  }),
  tagTypes: ['RbacOrganization'],
  endpoints: builder => ({
    getRbacOrganizationById: builder.query<OrganizationReadModel, string>({
      query: id => ({ url: `/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'RbacOrganization', id }],
    }),
    getRbacOrganizationLogo: builder.query<Blob, string>({
      query: id => ({ url: `/${id}/logo`, responseType: 'blob' }),
      providesTags: (_result, _error, id) => [{ type: 'RbacOrganization', id }],
    }),
    updateRbacOrganization: builder.mutation<void, UpdateRbacOrganizationArgs>({
      query: ({ data, logo }) => {
        const formData = new FormData();
        formData.append('json', JSON.stringify(data));
        if (logo) {
          formData.append('logo', logo, logo.name);
        }

        return {
          url: `/${data.id}`,
          method: 'PUT',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
      invalidatesTags: (_result, _error, { data }) => [
        { type: 'RbacOrganization', id: data.id },
      ],
      // The header switcher and the organization context read organizationsApi, so a renamed organization
      // would keep its old name there until a reload.
      async onQueryStarted({ data }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            organizationsApi.util.invalidateTags([
              'Organizations',
              { type: 'Organization', id: data.id },
            ]),
          );
        } catch {
          // The failed save is reported by the form.
        }
      },
    }),
  }),
});

export const {
  useGetRbacOrganizationByIdQuery,
  useGetRbacOrganizationLogoQuery,
  useUpdateRbacOrganizationMutation,
} = organizationApi;
